<template>
  <div class="unit-details">

    <div class="page-content-state page-content-state-loading mt-5" v-if="loading">
      <span class="page-content-state__icon  far fa-spinner-third fa-spin"></span>
      <div class="page-content-state__content">Loading unit {{ relationship.code }}</div>
    </div>

    <template v-else-if="error">
      <div class="unit-details__head">
        <button type="button" class="btn-link-bold mb-3" v-if="canGoBack" v-on:click="back()">
          <i class="far fa-chevron-left mr-2"></i> Back to unit selection
        </button>
        <div class="page-content-state">
          <span class="page-content-state__icon  fal fa-sad-cry"></span>
          <div class="page-content-state__header">Sorry, there was a problem loading unit details.</div>
          <button type="button" class="btn btn-primary mt-3" v-on:click="loadUnit()">Try again</button>
        </div>
      </div>
    </template>

    <template v-else-if="unit">
      <div class="unit-details__head">

        <button type="button" class="btn-link-bold mb-3" v-if="canGoBack" v-on:click="back()">
          <i class="far fa-chevron-left mr-2"></i> Back to unit selection
        </button>

        <div class="unit-details__head-content">
          <div class="unit-details__head--info">
            {{unit.code}}
            <h4 class="mb-2">{{unit.name}}</h4>
            <div class="info-details">
              <div>
                {{unit.credits}} credit points
              </div>
              <div v-if="fees">
                <strong>${{fees}}</strong>
                indicative fee
              </div>
            </div>
            <div v-if="isReviewer">
              <div v-if="isDiscontinued || isArchived" class="badge badge-danger" :class="isDiscontinued ? '' : ''">{{ statusLabel(this.relationship.masterStatus) }}</div>
              <div v-else class="badge mr-1" :class="this.relationship.status != 'Active' ? 'badge-primary' : 'badge-success'">{{ statusLabel(this.relationship.status) }}</div>
              <div class="badge badge-secondary">Version {{ this.relationship.courseLoopVersionNumber || this.relationship.version }}</div>
            </div>
          </div>
          <div class="unit-details__head-controls" v-if="!reviewMode">
            <button type="button" :class="['btn','small-padding','mb-2', selected? 'btn-tertiary' :'btn-primary']"
              v-if="allowEdit"
              v-on:click="toggleUnitSelection">
              <i class="fal fa-plus" v-if="!selected"></i>
              <i class="fal fa-trash-alt" v-if="selected"></i>
              <span class="text">{{selected? 'Remove from' : 'Add to my'}} Course Plan</span>
            </button>
            <button type="button" :class="['btn','btn-secondary', 'small-padding', completed ? 'complete' : '']" v-if="canComplete"
              v-on:click="toggleUnitComplete">
              <i class="far fa-check-circle" v-if="!completed"></i>
              <i class="fas fa-check-circle" v-if="completed"></i>
              <span class="text">{{completed? 'Unit completed' : 'Mark as complete'}}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="unit-details__body">

        <div class="overview" v-if="description">
          <h4>Overview</h4>
          {{description | cleanHTML}}
        </div>
        <div class="offerings" v-if="!reviewMode && !loading && offerings && offerings.length">
          <h4>Offerings</h4>
          <div class="row details">
            <div class="offering col-6" v-for="offering in offerings" v-bind:key="offering.id">
              <div class="attendance-mode">{{offering.attendanceMode}}</div>
              <div class="teaching-scheduled">
                <div v-for="(tS) in offering.teachingScheduled" v-bind:key="tS">
                  {{tS}}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="requisites-section" v-if="requisites && requisites.length">
          <h4>Requisites</h4>
          <div class="requisites-section-type" v-for="req in requisites" v-bind:key="req.type">
            <h5>{{req.type}}</h5>
            <SlideOutUnitDetailsPreReq v-for="(subcontainer, index) in req.containers" v-bind:key="index"
              :lastElement="index >= (req.containers.length -1)" :container="subcontainer" :displayRequirements="req.type === 'Prerequisite'" :displayExclusions="req.type === 'Exclusion' || req.type === 'Equivalent'"
              :doPreReq="true" />
          </div>
        </div>
        <div class="enrolment-rules-section" v-if="enrolmentRules">
          <h4>Enrolment Rules</h4>
          <p class="description" v-for="req in enrolmentRules">{{req.description | cleanHTML}}</p>
          <div class="page-alert warning">
            <i class="page-alert__icon far fa-exclamation-circle"></i>
            <div>
              <div class="page-alert__body">
                <p>Course Visualiser cannot apply enrolment rules. Please carefully review and ensure you meet the enrolment criteria for the units in this course.</p>
              </div>
            </div>
          </div>
        </div>
        <div class="assessments" v-if="unit.assessments && unit.assessments.length">
          <h4>Assessments</h4>
          <div class="details" v-for="(assessment, index) in unit.assessments" v-bind:key="index">
            <div class="head">
              <div class="title">
                {{assessment.name}}
              </div>
            </div>
            <div class="body">
              <p class="description">{{assessment.description | cleanHTML}}</p>
              <ul class="info">
                <li v-if="assessment.weight">
                  <label>Weight: </label> {{assessment.weight}}
                </li>
                <li v-if="assessment.due">
                  <label>Due: </label> {{assessment.due}}
                </li>
                <li v-if="assessment.individualGroup">
                  <label>Individual / Group: </label> {{assessment.individualGroup}}
                </li>
                <li v-if="assessment.unitLearningOutcomeCodes && assessment.unitLearningOutcomeCodes.length">
                  <label>Unit learning outcomes: </label> {{assessment.unitLearningOutcomeCodes.join(", ")}}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="activities" v-if="learningActivities && learningActivities.length">
          <h4>Learning activities</h4>
          <div class="activities-container" v-for="(container, index) in learningActivities" v-bind:key="index">
            <p>{{container.description}}</p>
            <div class="activity" v-for="(activity, index) in container.activities" v-bind:key="index">
              <p>{{activity.activity}}</p>
              <p v-if="activity.numberOfSessions">Number of sessions: {{ activity.numberOfSessions }}</p>
              <p>Duration: {{activity.duration}}</p>
              <p>Frequency: {{activity.frequency}}</p>
            </div>
          </div>
        </div>

        <div class="activities" v-if="integratedLearning && integratedLearning.length">
          <h4>Work integrated learning</h4>
          <p>{{ integratedLearning }}</p>
        </div>

        <div class="contacts" v-if="contacts && contacts.length">
          <h4>Academic contacts</h4>
          <div class="details" v-for="contact in contacts" v-bind:key="contact.email">
            <h5>{{contact.type}}</h5>
            <label>Name: </label> {{contact.name}}<br />
            <label>Email: </label> <a class="link-highlight" :href="'mailto:'+contact.email">{{contact.email}}</a>
          </div>
        </div>

        <div class="handbook">
          <a :href="`https://api.murdoch.edu.au/handbook/v2/gotohandbook/${ relationship.code }`" target="_blank" class="link-highlight">
            For more information on this unit, please view the Handbook
          </a>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import HtmlHelper from 'common/helpers/html'
import { dynamicSort } from 'common/helpers/dynamic-sort'
import Curriculum from 'client/js/models/curriculum'
import StudentPreferences from 'client/js/models/student-preferences.js'
import SlideOutUnitDetailsPreReq from './SlideOutUnitDetailsPreReq.vue'
import inputBuilder from 'common/helpers/input-builder.js'

export default {
  props: {
    relationship: {
      type: Object,
      required: true
    },
    location: {
      type: String,
      required: true
    },
    firstLevelTitle: {
      type: String,
      required: true
    },
    allowEdit: {
      type: Boolean,
      required: true
    },
    canComplete: {
      type: Boolean,
      required: false,
      default: true
    },
    verticalGrouping: {
      type: String,
      required: false,
      default: ''
    },
    canGoBack: {
      type: Boolean,
      required: false
    },
    reviewMode: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  filters: {
    cleanHTML: (text) => {
      return text != null ? HtmlHelper.removeTags(text) : ''
    }
  },
  data() {
    return {
      loading: false,
      error: null
    }
  },
  computed: {
    ...mapGetters({
      isReviewer: 'global/isReviewer',

      course: 'courses/getSelectedCourse',

      feesPreferences: 'preferences/getFeesPreferences',
      selectedPreferences: 'preferences/getSelectedStudyPreferences',

      items: 'units/getSelectedCurriculumItems',
      totalCredits: 'units/getTotalCredits',
      completedUnits: 'units/getCompletedUnits'
    }),
    fees() {
      return this.feesPreferences && this.feesPreferences.calculator && this.feesPreferences.calculator.calculate
        ? this.feesPreferences.calculator.calculate(this.unit)
        : null
    },
    unit() {
      return this.relationship.unit
    },
    selected() {
      return this.items.filter((item) => item.id === this.relationship.id && item.location === this.location).length > 0 ? true : false
    },
    duplicate() {
      const result = this.items.find((item) => item.id === this.relationship.id)
      return result && result.location != this.location
    },
    completed() {
      return this.completedUnits.includes(this.relationship.code)
    },
    convertedExclusions() {
      return inputBuilder.convertRequisites(this.requisites, "Exclusion")
    },
    description() {
      return this.unit && this.unit.unitDescriptions && this.unit.unitDescriptions.length && this.unit.unitDescriptions[0].description
        ? this.unit.unitDescriptions[0].description
        : ''
    },
    requisites() {
      let requisites = []
      if (this.unit && this.unit.requisites) {
        this.unit.requisites.forEach((req) => {
          let existing = requisites.find((r) => r.type === req.type)
          if (!existing) {
            existing = {
              type: req.type,
              containers: []
            }
            requisites.push(existing)
          }
          existing.containers = existing.containers.concat(req.containers)
        })
      }
      return requisites
    },
    validLocations() {
      if (this.course && this.selectedPreferences) {
        return StudentPreferences.getCourseLocations(this.course, this.selectedPreferences.studentType, this.selectedPreferences.placeOfResidence)
      }
      return []
    },
    offerings() {
      let offerings = []
      if (this.unit && this.unit.offerings) {
        let validUnitOfferings = this.unit.offerings.filter(
          (offering) => offering.location && offering.location.id && this.validLocations.includes(offering.location.id)
        )
        validUnitOfferings.forEach((offering) => {
          let existingOffering = offerings.find((o) => o.attendanceMode === offering.attendanceMode)
          if (!existingOffering) {
            existingOffering = {
              attendanceMode: offering.attendanceMode,
              teachingScheduled: []
            }
            offerings.push(existingOffering)
          }

          var offeringDisplay = offering.displayName + (offering.endYear ? '' : '-CURRENT')

          if (!existingOffering.teachingScheduled.includes(offeringDisplay)) existingOffering.teachingScheduled.push(offeringDisplay)

          existingOffering.teachingScheduled = existingOffering.teachingScheduled.sort()
        })
      }

      // only return offerings that has valid teaching scheduled
      return offerings.filter((o) => o.teachingScheduled && o.teachingScheduled.length)
    },
    learningOutcomes() {
      return this.unit && this.unit.unitLearningOutcomes && this.unit.unitLearningOutcomes.length
        ? this.unit.unitLearningOutcomes.sort(dynamicSort('Prerequisite'))
        : []
    },
    enrolmentRules() {
      return this.unit && this.unit.enrolmentRules && this.unit.enrolmentRules.length
        ? this.unit.enrolmentRules // .sort(dynamicSort('type'))
        : null
    },
    learningActivities() {
      let activities = []
      if (this.unit && this.unit.timetabledLearningActivities) {
        activities.push({
          description: 'Timetabled learning activities',
          activities: this.unit.timetabledLearningActivities
        })
      }
      return activities
    },
    integratedLearning() {
      return this.unit && this.unit.workIntegratedLearning && this.unit.workIntegratedLearning.length && this.unit.workIntegratedLearning[0]
        ? this.unit.workIntegratedLearning[0]
        : ''
    },
    contacts() {
      const contacts = []
      if (this.unit) {
        if (this.unit.administrativeContacts && this.unit.administrativeContacts.length) {
        }

        if (this.unit.unitCoordinators && this.unit.unitCoordinators.length) {
          this.unit.unitCoordinators.forEach((c) => {
            contacts.push({
              name: c.name,
              email: c.email,
              type: 'Unit Coordinator'
            })
          })
        }
      }

      return contacts
    },
    handbookUrl() {
      try {
        return Curriculum.getRelationshipHandbookUrl(this.relationship)
      } catch (err) {
        this.error = err
      }
    },
    isDiscontinued() {
      // console.log('isDiscontinued', this.relationship)
      return this.relationship?.masterStatus == 'Discontinued'
    },
    isArchived() {
      // console.log('isArchived', this.relationship)
      return this.relationship?.masterStatus == 'Archived'
    }
  },
  methods: {
    ...mapActions({
      addItem: 'units/addCurriculumItem',
      removeItem: 'units/removeCurriculumItem',
      updateUnitCompletion: 'units/updateUnitCompletion'
    }),
    back() {
      this.$emit('back')
    },
    statusLabel(id) {
      return this.__(`label:status ${id.toLowerCase()}`)
    },
    toggleUnitSelection() {
      const args = {
        firstLevelTitle: this.firstLevelTitle,
        relationship: this.relationship,
        location: this.location,
        verticalGrouping: this.verticalGrouping
      }
      if (!this.selected) {
        this.addItem(args)
      } else {
        this.removeItem(args)
      }
    },
    toggleUnitComplete() {
      this.updateUnitCompletion({
        id: this.unit.id,
        code: this.unit.code,
        completed: !this.completed
      })
      if (this.completed && this.allowEdit && !this.selected) {
        this.toggleUnitSelection()
      }
    },
    async loadUnit() {
      this.loading = true
      this.error = null
      if (!this.unit) {
        try {
          const unit = await Curriculum.getUnit(this.relationship.code, this.relationship.version)
          if (unit) {
            Vue.set(this.relationship, 'unit', unit)
          }
        } catch (err) {
          this.error = err
        }
      }
      this.loading = false
    }
  },
  mounted: async function () {
    await this.loadUnit()
  },
  components: { SlideOutUnitDetailsPreReq }
}
</script>
